import {
  Autocomplete,
  Button,
  Flex,
  Grid,
  Label,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import { listCompanies } from "graphql/queries";
import SelectUser from "components/users/SelectUser";

const STATUS_OPTIONS = ["", "Activo", "Inactivo", "Archivo"];

const RecipeForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [form, setForm] = useState(initialValues || {});
  const [companies, setCompanies] = useState({ list: [], loading: false });
  const [errors, setErrors] = useState({
    name: "",
    companyID: "",
  });

  const validateErrors = useErrors({
    name: "isRequired",
    companyID: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(form).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (checkIncorrectValue("companyID", companies.list)) return;

    if (!hasErrors) onSubmit(form);
  };

  const checkIncorrectValue = (name, list) => {
    if (!list.some((rec) => rec.id === form[name])) {
      setErrors((prevSt) => ({
        ...prevSt,
        [name]: strings.validations.incorrectValue,
      }));
      return true;
    }
    return false;
  };

  const selectResponsible = (responsibleID) => {
    setForm((prevSt) => ({ ...prevSt, performedById: responsibleID }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    if (name === "companyID") {
      setForm((prevSt) => ({
        ...prevSt,
        [name]: value,
      }));
      checkErrors(name, value);
      return;
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
    checkErrors(name, value);
  };

  const onClearSelect = (name) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      setForm((prevSt) => ({
        ...prevSt,
        [name]: value,
      }));
      return;
    }
    setForm((prevSt) => ({ ...prevSt, [name]: value }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({ name: "", companyID: "" });
    setForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompanies({ list: [], loading: true });

      if (me?.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me?.managedCompanies?.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [{ id: { contains: id } }, { name: { contains: name } }],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies((prev) => ({ ...prev, list: companiesItems }));
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    } finally {
      setCompanies((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    setForm(initialValues || {});
    fetchCompanies({ id: initialValues?.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={form.name || ""}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          />
          <TextField
            name="code"
            label={strings.fields.code.label}
            value={form.code || ""}
            onChange={onChange}
          />
        </Grid>

        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="date"
            label={strings.fields.date.label}
            value={form.date || ""}
            onChange={onChange}
            type="date"
          />
          <TextField
            name="type"
            label={strings.fields.type.label}
            value={form.type || ""}
            onChange={onChange}
          />
        </Grid>

        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <Flex direction="column" gap="xs">
            <Label htmlFor="companyID">
              <LabelRequired text={strings.fields.company.label} />
            </Label>
            <Autocomplete
              id="companyID"
              name="companyID"
              isRequired
              placeholder={strings.fields.company.placeholder}
              value={
                companies.list.find(
                  (companyRec) => companyRec.id === form.companyID
                )?.name ??
                form.companyID ??
                ""
              }
              options={companies.list.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companies.loading}
              isDisabled={type === 'update'}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors.companyID}
              hasError={!!errors.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            />
          </Flex>
          <TextField
            name="inputId"
            label={strings.fields.inputId.label}
            placeholder={strings.fields.inputId.placeholder}
            value={form.inputId || ""}
            onChange={onChange}
          />
        </Grid>

        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="availableInventoryQuantity"
            label={strings.fields.availableInventoryQuantity.label}
            value={form.availableInventoryQuantity || ""}
            onChange={onChange}
            type="number"
          />
          <TextField
            name="inventoryUnit"
            label={strings.fields.inventoryUnit.label}
            value={form.inventoryUnit || ""}
            onChange={onChange}
          />
        </Grid>

        <Grid gap="15px" templateColumns={{ base: "1fr", large: "2fr 2fr" }}>
          <TextField
            name="inputQuantityToUse"
            label={strings.fields.inputQuantityToUse.label}
            value={form.inputQuantityToUse || ""}
            onChange={onChange}
            type="number"
          />
          <TextField
            name="applicationUnit"
            label={strings.fields.applicationUnit.label}
            value={form.applicationUnit || ""}
            onChange={onChange}
          />
        </Grid>

        <TextAreaField
          name="recipeDetails"
          label={strings.fields.recipeDetails.label}
          value={form.recipeDetails || ""}
          onChange={onChange}
        />

        <TextAreaField
          name="preparationProcedure"
          label={strings.fields.preparationProcedure.label}
          value={form.preparationProcedure || ""}
          onChange={onChange}
        />

        <Grid gap="15px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
          <div>
            <Label style={{ display: "block", marginBottom: 6 }}>
              {strings.fields.responsible.label}
            </Label>
            <SelectUser
              value={form.performedById}
              placeholder={strings.fields.responsible.label}
              onSelect={selectResponsible}
            />
          </div>
          <SelectField
            name="status"
            label={strings.fields.status.label}
            value={form.status || ""}
            onChange={onChange}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </SelectField>
        </Grid>

        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          />
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          />
        </Flex>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: { label: "Nombre" },
      code: { label: "Código" },
      date: { label: "Fecha" },
      type: { label: "Tipo" },
      company: { label: "Empresa", placeholder: "Buscar empresa..." },
      inputId: { label: "Insumo (ID)", placeholder: "ID de insumo" },
      availableInventoryQuantity: { label: "Cantidad disponible" },
      inventoryUnit: { label: "Unidad inventario" },
      inputQuantityToUse: { label: "Cantidad a usar" },
      applicationUnit: { label: "Unidad aplicación" },
      recipeDetails: { label: "Detalles" },
      preparationProcedure: { label: "Procedimiento preparación" },
      responsible: { label: "Responsable" },
      status: { label: "Estado" },
      buttons: { create: "Crear", save: "Guardar", reset: "Restablecer" },
    },
    validations: { incorrectValue: "Valor incorrecto" },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
    },
  },
});

export default RecipeForm;
